import React, { useEffect, useRef, useState, useCallback } from 'react';
import { log } from './lib/logger';
import TikTokChatView from './tiktok/TikTokChatView';

// Joins are shown one at a time (~0.7s each); busy streams get many per second, so keep only the latest
const MAX_TICKER_QUEUE = 20;

// A dropped connection (service restart, TikTok drop) retries soon; offline channels recheck once a minute
const DROP_RETRY_DELAYS_MS = [5000, 15000, 60000];

const TikTokChat = ({ username, active }) => {
    const [messages, setMessages] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState('connecting'); // 'connecting', 'connected', 'offline'
    const [disconnectError, setDisconnectError] = useState(null); // Track disconnection errors separately
    const [viewerCount, setViewerCount] = useState(0);
    const [totalLikeCount, setTotalLikeCount] = useState(0);
    const wsRef = useRef(null);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const userScrolledRef = useRef(false);
    const reconnectTimeoutRef = useRef(null);
    const connectionTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const intentionalCloseRef = useRef(false); // Track if we're intentionally closing the connection
    const wasConnectedRef = useRef(false); // Track if we were previously connected
    const definitiveOfflineRef = useRef(false); // Track if offline status is definitive (from service) vs uncertain (timeout/error)
    const MAX_RECONNECT_ATTEMPTS = 2; // offline/timeout: 2 rechecks a minute apart (each costs a TikTok lookup)
    const droppedRef = useRef(false); // the last disconnect was an unexpected drop, not an offline channel

    // Ticker state management
    const [tickerEnabled, setTickerEnabled] = useState(() => {
        const stored = sessionStorage.getItem('tiktokTickerEnabled');
        return stored ? JSON.parse(stored) : false;
    });
    const tickerEnabledRef = useRef(tickerEnabled); // Keep ref in sync
    const [tickerQueue, setTickerQueue] = useState([]);
    const [currentTickerMessage, setCurrentTickerMessage] = useState(null);
    const tickerIntervalRef = useRef(null);

    // Chat only filter state
    const [chatOnlyFilter, setChatOnlyFilter] = useState(() => {
        const stored = sessionStorage.getItem('tiktokChatOnlyFilter');
        return stored ? JSON.parse(stored) : false;
    });

    // New messages indicator
    const [hasNewMessages, setHasNewMessages] = useState(false);

    // Keep ref in sync with state
    useEffect(() => {
        tickerEnabledRef.current = tickerEnabled;
    }, [tickerEnabled]);

    const addMessage = useCallback((message) => {
        setMessages(prev => {
            // Check if message with this ID already exists
            if (prev.some(msg => msg.id === message.id)) {
                return prev; // Skip duplicate
            }

            // Content-based deduplication for different message types
            let isDuplicate = false;

            switch (message.type) {
                case 'follow':
                case 'share':
                    // One-time events - deduplicate by user
                    isDuplicate = prev.some(msg =>
                        msg.type === message.type &&
                        msg.user?.uniqueId === message.user?.uniqueId
                    );
                    break;

                case 'member':
                    // Time-based deduplication: only prevent joins from same user within 1 second
                    // This prevents immediate duplicates while allowing users to rejoin later
                    const now = Date.now();
                    const oneSecondAgo = now - 1000;
                    isDuplicate = prev.some(msg =>
                        msg.type === 'member' &&
                        msg.user?.uniqueId === message.user?.uniqueId &&
                        msg.timestamp > oneSecondAgo
                    );
                    break;

                case 'like':
                    // Deduplicate by user + likeCount + totalLikeCount
                    isDuplicate = prev.some(msg =>
                        msg.type === 'like' &&
                        msg.user?.uniqueId === message.user?.uniqueId &&
                        msg.likeCount === message.likeCount &&
                        msg.totalLikeCount === message.totalLikeCount
                    );
                    break;

                case 'gift':
                    // Deduplicate by user + gift ID + repeat count
                    isDuplicate = prev.some(msg =>
                        msg.type === 'gift' &&
                        msg.user?.uniqueId === message.user?.uniqueId &&
                        msg.gift?.giftId === message.gift?.giftId &&
                        msg.gift?.repeatCount === message.gift?.repeatCount
                    );
                    break;

                case 'chat':
                    // Deduplicate by user + message text within last 1 second only
                    // This prevents immediate duplicates but allows same message later
                    const chatNow = Date.now();
                    const chatOneSecondAgo = chatNow - 1000;
                    isDuplicate = prev.some(msg =>
                        msg.type === 'chat' &&
                        msg.user?.uniqueId === message.user?.uniqueId &&
                        msg.message === message.message &&
                        msg.timestamp > chatOneSecondAgo
                    );
                    break;

                case 'emote':
                    // Deduplicate by user + emote within last 500ms only
                    const emoteNow = Date.now();
                    const halfSecondAgo = emoteNow - 500;
                    isDuplicate = prev.some(msg =>
                        msg.type === 'emote' &&
                        msg.user?.uniqueId === message.user?.uniqueId &&
                        msg.emote?.emoteId === message.emote?.emoteId &&
                        msg.timestamp > halfSecondAgo
                    );
                    break;

                case 'system':
                    // Deduplicate system messages by message content
                    isDuplicate = prev.some(msg =>
                        msg.type === 'system' &&
                        msg.message === message.message
                    );
                    break;

                // Other message types only use ID-based deduplication
                default:
                    break;
            }

            if (isDuplicate) {
                return prev; // Skip duplicate
            }

            const newMessages = [...prev, message];
            // Keep last 200 messages
            if (newMessages.length > 200) {
                return newMessages.slice(-200);
            }
            return newMessages;
        });
    }, []);

    const handleMessage = useCallback((data) => {
        switch (data.type) {
            case 'subscribed':
                setConnectionStatus('connected');
                wasConnectedRef.current = true; // Mark that we successfully connected
                log.debug('[TikTok] Subscribed to', username);

                // Clear connection timeout since we got a response
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }

                // Clear any pending reconnect attempts since we're now connected
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                // Reset reconnect attempts on successful connection
                reconnectAttemptsRef.current = 0;
                droppedRef.current = false;
                break;

            case 'chat':
                if (data.data.emotes?.length) {
                    // Rare and hard to trigger: log real examples to confirm how `place` counts
                    log.debug('[TikTok] Chat with subscriber emotes:', { message: data.data.message, emotes: data.data.emotes });
                }
                addMessage({
                    id: data.data.id,
                    type: 'chat',
                    user: data.data.user,
                    message: data.data.message,
                    emotes: data.data.emotes || [],
                    timestamp: data.data.timestamp
                });
                break;

            case 'gift':
                addMessage({
                    id: data.data.id,
                    type: 'gift',
                    user: data.data.user,
                    gift: data.data.gift,
                    timestamp: data.data.timestamp
                });
                break;

            case 'follow':
                addMessage({
                    id: data.data.id,
                    type: 'follow',
                    user: data.data.user,
                    timestamp: data.data.timestamp
                });
                break;

            case 'share':
                addMessage({
                    id: data.data.id,
                    type: 'share',
                    user: data.data.user,
                    timestamp: data.data.timestamp
                });
                break;

            case 'stats':
                setViewerCount(data.data.viewerCount);
                break;

            case 'member':
                if (tickerEnabledRef.current) {
                    // Add to ticker queue instead of main feed (with deduplication)
                    setTickerQueue(prev => {
                        // Check if message with this ID already exists in queue
                        if (prev.some(msg => msg.id === data.data.id)) {
                            return prev; // Skip duplicate
                        }
                        // Also check for duplicate user joins
                        if (prev.some(msg => msg.user?.uniqueId === data.data.user?.uniqueId)) {
                            return prev; // Skip duplicate user join
                        }
                        return [...prev, {
                            id: data.data.id,
                            type: 'member',
                            user: data.data.user,
                            timestamp: data.data.timestamp
                        }].slice(-MAX_TICKER_QUEUE);
                    });
                } else {
                    // Add to main feed as normal
                    addMessage({
                        id: data.data.id,
                        type: 'member',
                        user: data.data.user,
                        timestamp: data.data.timestamp
                    });
                }
                break;

            case 'like':
                if (data.data.totalLikeCount) {
                    setTotalLikeCount(data.data.totalLikeCount);
                }
                addMessage({
                    id: data.data.id,
                    type: 'like',
                    user: data.data.user,
                    likeCount: data.data.likeCount,
                    totalLikeCount: data.data.totalLikeCount,
                    timestamp: data.data.timestamp
                });
                break;

            case 'emote':
                addMessage({
                    id: data.data.id,
                    type: 'emote',
                    user: data.data.user,
                    emote: data.data.emote,
                    timestamp: data.data.timestamp
                });
                break;

            case 'envelope':
                addMessage({
                    id: data.data.id,
                    type: 'envelope',
                    coins: data.data.coins,
                    canOpen: data.data.canOpen,
                    timestamp: data.data.timestamp
                });
                break;

            case 'question':
                addMessage({
                    id: data.data.id,
                    type: 'question',
                    user: data.data.user,
                    questionText: data.data.questionText,
                    timestamp: data.data.timestamp
                });
                break;

            case 'linkMicBattle':
                addMessage({
                    id: data.data.id,
                    type: 'linkMicBattle',
                    battleUsers: data.data.battleUsers,
                    timestamp: data.data.timestamp
                });
                break;

            case 'linkMicArmies':
                addMessage({
                    id: data.data.id,
                    type: 'linkMicArmies',
                    battleStatus: data.data.battleStatus,
                    armies: data.data.armies,
                    timestamp: data.data.timestamp
                });
                break;

            case 'liveIntro':
                addMessage({
                    id: data.data.id,
                    type: 'liveIntro',
                    intro: data.data.intro,
                    timestamp: data.data.timestamp
                });
                break;

            case 'superFan':
                addMessage({
                    id: data.data.id,
                    type: 'superFan',
                    user: data.data.user,
                    timestamp: data.data.timestamp
                });
                break;

            case 'streamEnd':
                setConnectionStatus('offline');
                definitiveOfflineRef.current = true; // Stream explicitly ended
                addMessage({
                    id: `system-${Date.now()}`,
                    type: 'system',
                    message: 'Stream has ended'
                });
                break;

            case 'error':
                console.error('[TikTok] Error:', data.message, data.code);
                const errorMsg = data.message?.toLowerCase() || '';
                const errorCode = data.code;

                // The service is full, or this page asked for too many TikTok channels: say so, and let
                // the normal retry (or the Reconnect button) try again later
                if (errorCode === 'AT_CAPACITY' || errorCode === 'TOO_MANY_CHANNELS') {
                    if (connectionTimeoutRef.current) {
                        clearTimeout(connectionTimeoutRef.current);
                        connectionTimeoutRef.current = null;
                    }
                    setConnectionStatus('offline');
                    setDisconnectError(data.message || 'TikTok chat is unavailable right now');
                    break;
                }

                // Check if error indicates channel is offline or stream ended
                // Use error code if available (more reliable), otherwise fall back to message parsing
                const isOfflineError = errorCode === 'USER_NOT_LIVE' ||
                                     errorCode === 'LIVE_HAS_ENDED' ||
                                     errorCode === 'NOT_LIVE' ||
                                     errorCode === 'CONNECTION_UNAVAILABLE' ||
                                     errorMsg.includes('offline') ||
                                     errorMsg.includes('ended') ||
                                     errorMsg.includes('not currently live') ||
                                     errorMsg.includes('not live') ||
                                     errorMsg.includes('stream has ended') ||
                                     errorMsg.includes('unavailable') ||
                                     errorMsg.includes('blocked');

                if (isOfflineError) {
                    setConnectionStatus('offline');
                    definitiveOfflineRef.current = true; // Service explicitly said channel is not live

                    // Clear connection timeout since we got a definitive response
                    if (connectionTimeoutRef.current) {
                        clearTimeout(connectionTimeoutRef.current);
                        connectionTimeoutRef.current = null;
                    }
                }
                break;

            default:
                break;
        }
    }, [username, addMessage]);

    const cleanupWebSocket = useCallback((ws) => {
        if (!ws) return;

        // Remove all event handlers to prevent them from firing
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;

        // Send unsubscribe if connection is still open
        if (ws.readyState === WebSocket.OPEN) {
            try {
                ws.send(JSON.stringify({
                    type: 'unsubscribe',
                    username: username
                }));
            } catch (error) {
                console.error('[TikTok] Error sending unsubscribe:', error);
            }
        }

        // Close the connection
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            try {
                ws.close();
            } catch (error) {
                console.error('[TikTok] Error closing WebSocket:', error);
            }
        }
    }, [username]);

    const connectToService = useCallback(() => {
        // Clean up any existing WebSocket first
        if (wsRef.current) {
            intentionalCloseRef.current = true; // Mark as intentional so onclose doesn't trigger errors
            cleanupWebSocket(wsRef.current);
            wsRef.current = null;
        }

        // Reset state when connecting
        setConnectionStatus('connecting');
        setDisconnectError(null); // Clear any disconnect errors when reconnecting
        intentionalCloseRef.current = false; // Reset the flag when starting a new connection
        definitiveOfflineRef.current = false; // Reset offline status - we're trying again

        // Clear any existing connection timeout
        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
        }

        // Set a timeout to mark as offline if we don't get a response
        connectionTimeoutRef.current = setTimeout(() => {
            log.debug('[TikTok] Connection timeout - no response from server');
            setConnectionStatus('offline');
        }, 10000); // 10 second timeout

        // Connect to TikTok service
        const wsUrl = process.env.REACT_APP_TIKTOK_WS_URL || 'ws://localhost:8083';
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            log.debug('[TikTok] Connected to service');
            ws.send(JSON.stringify({
                type: 'subscribe',
                username: username
            }));
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                handleMessage(data);
            } catch (error) {
                console.error('[TikTok] Parse error:', error);
            }
        };

        ws.onerror = (error) => {
            console.error('[TikTok] WebSocket error:', error);
        };

        ws.onclose = () => {
            log.debug('[TikTok] Disconnected');

            // Only show disconnect error if this was NOT an intentional close AND we were previously connected
            const wasIntentional = intentionalCloseRef.current;
            const hadConnected = wasConnectedRef.current;

            intentionalCloseRef.current = false; // Reset the flag

            // If this was an unexpected disconnect, trigger offline status to enable reconnection
            if (!wasIntentional && hadConnected) {
                droppedRef.current = true;
                setConnectionStatus('offline');
                setDisconnectError('TikTok has disconnected');
            } else {
                // Only set to connecting if we weren't explicitly marked as offline
                setConnectionStatus(prev => prev === 'offline' ? 'offline' : 'connecting');
            }
        };
    }, [username, handleMessage, cleanupWebSocket]);

    useEffect(() => {
        if (!username) return;

        // Reset reconnect attempts when changing channels
        reconnectAttemptsRef.current = 0;
        droppedRef.current = false;
        wasConnectedRef.current = false; // Reset connection state
        definitiveOfflineRef.current = false; // Reset offline status

        connectToService();

        return () => {
            // Clear any pending reconnect attempts
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            // Clear connection timeout
            if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current);
                connectionTimeoutRef.current = null;
            }

            // Mark as intentional close before closing
            intentionalCloseRef.current = true;

            // Clean up WebSocket connection
            if (wsRef.current) {
                cleanupWebSocket(wsRef.current);
                wsRef.current = null;
            }
        };
    }, [username, connectToService, cleanupWebSocket]);

    // Handle automatic reconnection for offline channels (limited attempts)
    useEffect(() => {
        if (connectionStatus === 'offline' && username) {
            // Don't auto-reconnect if the service definitively said it's offline
            if (definitiveOfflineRef.current) {
                log.debug('[TikTok] Channel is definitively offline (from service), not auto-reconnecting');
                return;
            }

            // A dropped connection retries quickly; an offline channel or timeout rechecks once a minute
            const dropped = droppedRef.current;
            const maxAttempts = dropped ? DROP_RETRY_DELAYS_MS.length : MAX_RECONNECT_ATTEMPTS;
            if (reconnectAttemptsRef.current >= maxAttempts) {
                log.debug('[TikTok] Max reconnect attempts reached, stopped retrying');
                return;
            }
            const delay = dropped ? DROP_RETRY_DELAYS_MS[reconnectAttemptsRef.current] : 60000;

            log.debug(`[TikTok] ${dropped ? 'Connection dropped' : 'Connection issue (timeout/error)'}, retrying in ${delay / 1000}s (attempt ${reconnectAttemptsRef.current + 1}/${maxAttempts})`);

            reconnectTimeoutRef.current = setTimeout(() => {
                reconnectAttemptsRef.current += 1;
                log.debug(`[TikTok] Reconnecting... (attempt ${reconnectAttemptsRef.current}/${maxAttempts})`);

                // Mark as intentional close before reconnecting
                intentionalCloseRef.current = true;

                // Reconnect (connectToService handles cleanup of old connection)
                connectToService();
            }, delay);
        }

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [connectionStatus, username, connectToService]);

    // Detect when user manually scrolls
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 300;

        // If user is at bottom, enable auto-scroll; otherwise disable it
        userScrolledRef.current = !isAtBottom;

        // Clear new messages indicator when scrolling to bottom
        if (isAtBottom) {
            setHasNewMessages(false);
        }
    }, []);

    // Toggle ticker enabled/disabled
    const toggleTicker = useCallback(() => {
        const updated = !tickerEnabled;
        setTickerEnabled(updated);
        sessionStorage.setItem('tiktokTickerEnabled', JSON.stringify(updated));

        // Clear queue when disabling
        if (!updated) {
            setTickerQueue([]);
            setCurrentTickerMessage(null);
            if (tickerIntervalRef.current) {
                clearTimeout(tickerIntervalRef.current);
                tickerIntervalRef.current = null;
            }
        }

        // Force scroll to bottom after toggle
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            }
        }, 0);
    }, [tickerEnabled]);

    // Toggle chat only filter
    const toggleChatOnlyFilter = useCallback(() => {
        const updated = !chatOnlyFilter;
        setChatOnlyFilter(updated);
        sessionStorage.setItem('tiktokChatOnlyFilter', JSON.stringify(updated));

        // Force scroll to bottom after toggle
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            }
        }, 0);
    }, [chatOnlyFilter]);

    // Scroll to bottom and clear new messages indicator
    const scrollToBottom = useCallback(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            userScrolledRef.current = false;
            setHasNewMessages(false);
        }
    }, []);

    // Manual reconnect function
    const handleManualReconnect = useCallback(() => {
        log.debug('[TikTok] Manual reconnect requested');

        // Reset reconnect attempts
        reconnectAttemptsRef.current = 0;

        // Clear any pending reconnect timeouts
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        // Clear connection timeout
        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
        }

        // Mark as intentional close before reconnecting
        intentionalCloseRef.current = true;

        // Reconnect (connectToService handles cleanup of old connection)
        connectToService();
    }, [connectToService]);

    useEffect(() => {
        // Only auto-scroll if user hasn't manually scrolled away
        if (!userScrolledRef.current && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            // Only update scrollTop (Y axis), preserve scrollLeft (X axis)
            container.scrollTop = container.scrollHeight;
            setHasNewMessages(false); // Clear indicator when auto-scrolling
        } else if (userScrolledRef.current && messages.length > 0) {
            // User is scrolled away and new messages arrived
            setHasNewMessages(true);
        }
    }, [messages]);

    // Ticker display logic
    useEffect(() => {
        if (!tickerEnabled) {
            if (tickerIntervalRef.current) {
                clearTimeout(tickerIntervalRef.current);
                tickerIntervalRef.current = null;
            }
            setCurrentTickerMessage(null);
            return;
        }

        // Show first message in queue if nothing is currently displayed
        if (!currentTickerMessage && tickerQueue.length > 0) {
            setCurrentTickerMessage(tickerQueue[0]);
            return;
        }

        // Set up timeout to clear current message after 1 second (only if not already set)
        if (currentTickerMessage && !tickerIntervalRef.current) {
            tickerIntervalRef.current = setTimeout(() => {
                setTickerQueue(prev => prev.slice(1)); // Remove current from queue
                setCurrentTickerMessage(null); // Clear current message
                tickerIntervalRef.current = null; // Clear the ref
            }, 700);
        }
    }, [tickerQueue, currentTickerMessage, tickerEnabled]);

    return (
        <TikTokChatView
            username={username}
            active={active}
            messages={messages}
            connectionStatus={connectionStatus}
            disconnectError={disconnectError}
            viewerCount={viewerCount}
            totalLikeCount={totalLikeCount}
            tickerEnabled={tickerEnabled}
            chatOnlyFilter={chatOnlyFilter}
            currentTickerMessage={currentTickerMessage}
            hasNewMessages={hasNewMessages}
            scrollContainerRef={scrollContainerRef}
            messagesEndRef={messagesEndRef}
            onScroll={handleScroll}
            onToggleTicker={toggleTicker}
            onToggleChatOnly={toggleChatOnlyFilter}
            onReconnect={handleManualReconnect}
            onScrollToBottom={scrollToBottom}
        />
    );
};

export default TikTokChat;